/**
 * Author Profile — the versioned, human-governed "voice record" the weekly
 * learning loop reads and (only with approval) updates.
 *
 * The operator's Claude ritual keeps a living picture of what works for
 * THIS author: the What-Works signal set, banned phrases, tone notes. This
 * module makes that picture a first-class, GOVERNED record:
 *
 *   • applySignalUpdate   — the pure state transition that installs a new
 *     signal set, bumping `version` and returning the honest
 *     `diffSignalSets` receipt of exactly what changed.
 *   • proposeProfileUpdate — learning is HUMAN-APPROVED, never silent
 *     drift: emits an actionDiff-shaped pending proposal (same envelope
 *     discipline as lib/agent/reversal + approvalQueue — deterministic id,
 *     `status: 'pending'`, an old→new diff line, a provenance note) and
 *     mutates NOTHING. Only when a human approves does the caller run
 *     applySignalUpdate.
 *   • revertProfile        — Time Machine for voice, via the reversal
 *     pattern: given the version history, restore an earlier version
 *     EXACTLY (a defensive copy, never a live reference). Unknown versions
 *     throw with an explicit reason — an honest wall, not a silent no-op.
 *
 * PURE by contract: all state is passed in; no store, no firebase, no
 * clock reads, no mutation of inputs.
 */

import { diffSignalSets, type SignalSet } from './whatWorks';

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

/** The versioned per-author voice record the learning loop governs. */
export type AuthorProfile = {
  authorId: string;
  /** Monotonic version — bumped by every applied signal update. */
  version: number;
  /** The What-Works signal set currently trusted for this author. */
  signalSet: SignalSet;
  /** Phrases the author never wants generated. */
  bannedPhrases: string[];
  /** Free-form tone guidance carried into prompts. */
  toneNotes: string[];
  /** Epoch ms of the last applied update. */
  updatedAt: number;
};

/** The signal-set diff receipt every update carries. */
export type ProfileDiff = ReturnType<typeof diffSignalSets>;

/**
 * The actionDiff-shaped, approval-queue-ready proposal for one profile
 * update. Same envelope discipline as every governed agent proposal:
 * deterministic id, `status: 'pending'`, an old→new `actionDiff` line
 * (the trust mechanic from lib/agent/actionDiff), evidence, and a note.
 * Emitting one changes NOTHING until a human approves it.
 */
export type ProfileUpdateProposal = {
  /** Deterministic — re-running the loop proposes the same update. */
  id: string;
  origin: 'learning_loop';
  status: 'pending';
  actionType: 'update_author_profile';
  authorId: string;
  fromVersion: number;
  toVersion: number;
  /** Old→new one-liner, same shape diffForAction renders on confirm cards. */
  actionDiff: string;
  /** The full structured signal-set diff backing the one-liner. */
  diff: ProfileDiff;
  /** The candidate signal set — applied only via applySignalUpdate. */
  nextSignalSet: SignalSet;
  /** Human-readable evidence lines for the approval card. */
  evidence: string[];
  /** Provenance note for the approvals queue. */
  note: string;
};

// ---------------------------------------------------------------------------
// applySignalUpdate — the pure, version-bumping state transition
// ---------------------------------------------------------------------------

/**
 * Install `next` as the profile's trusted signal set: bumps `version`,
 * stamps `updatedAt` from the signal set's own `computedAt` (no implicit
 * clock read), and returns the honest {@link diffSignalSets} receipt.
 * Pure — the input profile is never mutated.
 */
export function applySignalUpdate(
  profile: AuthorProfile,
  next: SignalSet,
): { next: AuthorProfile; diff: ProfileDiff } {
  const diff = diffSignalSets(profile.signalSet, next);
  return {
    next: {
      ...profile,
      version: profile.version + 1,
      signalSet: next,
      bannedPhrases: [...profile.bannedPhrases],
      toneNotes: [...profile.toneNotes],
      updatedAt: next.computedAt,
    },
    diff,
  };
}

// ---------------------------------------------------------------------------
// proposeProfileUpdate — learning is human-approved, never silent drift
// ---------------------------------------------------------------------------

/** Plural-safe count fragment, e.g. "2 signals". */
function n(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/**
 * Compile a pending, actionDiff-shaped proposal to move this profile from
 * its current signal set to `next`. Nothing is mutated and nothing is
 * applied — the caller routes the proposal through the approval queue and
 * runs {@link applySignalUpdate} only on approval. Deterministic: same
 * profile + candidate → same proposal id and diff.
 */
export function proposeProfileUpdate(
  profile: AuthorProfile,
  next: SignalSet,
): ProfileUpdateProposal {
  const diff = diffSignalSets(profile.signalSet, next);
  const fromVersion = profile.version;
  const toVersion = profile.version + 1;

  const actionDiff =
    `signals: v${fromVersion} (${n(profile.signalSet.signals.length, 'signal')}, ` +
    `${n(profile.signalSet.totalPosts, 'post')}) → v${toVersion} ` +
    `(${n(next.signals.length, 'signal')}, ${n(next.totalPosts, 'post')})`;

  const evidence: string[] = [
    `${n(diff.upgraded.length, 'signal')} upgraded`,
    `${n(diff.downgraded.length, 'signal')} downgraded`,
    `${n(diff.added.length, 'signal')} added`,
    ...diff.upgraded.map(
      (s) => `↑ ${s.dimension} "${s.value}" now ${s.strength} (n=${s.sampleSize})`,
    ),
    ...diff.downgraded.map(
      (s) => `↓ ${s.dimension} "${s.value}" now ${s.strength} (n=${s.sampleSize})`,
    ),
  ];

  return {
    id: `profile-update-${profile.authorId}-v${toVersion}`,
    origin: 'learning_loop',
    status: 'pending',
    actionType: 'update_author_profile',
    authorId: profile.authorId,
    fromVersion,
    toVersion,
    actionDiff,
    diff,
    nextSignalSet: next,
    evidence,
    note:
      `Weekly learning loop proposes voice update for ${profile.authorId}: ` +
      `${actionDiff}. Approve to apply; reject to keep v${fromVersion}.`,
  };
}

// ---------------------------------------------------------------------------
// revertProfile — Time Machine for voice (reversal pattern)
// ---------------------------------------------------------------------------

/**
 * Restore an earlier profile version EXACTLY from the passed-in history —
 * the same compensating-restore idea lib/agent/reversal compiles for record
 * writes, applied to voice. Returns a defensive copy (never a live
 * reference into `history`) so later edits can't rewrite the past. An
 * unknown version throws with an explicit reason — an honest wall, never a
 * silent no-op.
 */
export function revertProfile(history: AuthorProfile[], toVersion: number): AuthorProfile {
  const found = history.find((p) => p.version === toVersion);
  if (!found) {
    const versions = history.map((p) => p.version).join(', ') || 'none';
    throw new Error(
      `Cannot revert: version ${toVersion} is not in the profile history (have: ${versions}).`,
    );
  }
  return {
    ...found,
    signalSet: {
      ...found.signalSet,
      signals: found.signalSet.signals.map((s) => ({
        ...s,
        evidencePostIds: [...s.evidencePostIds],
      })),
    },
    bannedPhrases: [...found.bannedPhrases],
    toneNotes: [...found.toneNotes],
  };
}
